import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Camera, Upload, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import ChallengeCard from '../components/ChallengeCard';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROTOCOL_STEPS = [
  { num: '01', icon: Camera, title: 'Capture', description: 'Study the brief. Style and photograph your look to meet the challenge criteria.' },
  { num: '02', icon: Upload, title: 'Submit', description: 'Upload your look to the feed and submit it to the active challenge from the detail page.' },
  { num: '03', icon: Award, title: 'Earn', description: 'Top submissions earn exclusive badges, credits, and editorial features.' },
];

const ChallengesPage = () => {
  const { isAuthenticated } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchChallenges = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/challenges`);
      setChallenges(response.data);
    } catch {
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChallenges();
  }, [fetchChallenges]);

  const activeCount = challenges.filter(c => c.status === 'active').length;

  return (
    <div className="min-h-screen pt-20 bg-[#0a0a0a]" data-testid="challenges-page">
      {/* Hero Header */}
      <div className="py-20 md:py-28 px-6 md:px-12 border-b border-white/5 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto text-center">
          <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent mx-auto mb-8" />

          <div className="flex items-center justify-center gap-3 mb-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500">HEIR&reg; Corporate</p>
            {activeCount > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-400 text-[10px] uppercase tracking-wider font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                {activeCount} Active
              </span>
            )}
          </div>

          <h1 className="text-5xl md:text-6xl font-serif italic text-white mb-6">
            Style Challenges
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto leading-relaxed">
            HEIR&reg; Corporate Gifting challenges — curate premium essentials packages that elevate teams, impress clients, and set the standard for executive presence.
          </p>

          <div className="w-px h-12 bg-gradient-to-b from-transparent via-white/20 to-transparent mx-auto mt-8" />
        </div>
      </div>

      {/* How Challenges Work — Protocol */}
      <section className="py-20 px-6 md:px-12 bg-[#111111] border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">Protocol</p>
            <h2 className="text-3xl md:text-4xl font-serif italic text-white">How Challenges Work</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {PROTOCOL_STEPS.map(({ num, icon: Icon, title, description }) => (
              <div key={num} className="relative p-8 bg-[#0a0a0a] border border-white/5 hover:border-white/15 transition-all duration-500" data-testid={`protocol-step-${num}`}>
                <div className="absolute top-6 right-6 text-5xl font-serif italic opacity-[0.04] text-white pointer-events-none select-none">
                  {num}
                </div>
                <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-gray-400" />
                </div>
                <h3 className="text-lg font-serif italic text-white mb-3">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Active Briefs */}
      <section className="py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-6">Current Briefs</p>
            <h2 className="text-3xl md:text-4xl font-serif italic text-white">Active Challenges</h2>
          </div>

          <ChallengesContent loading={loading} challenges={challenges} />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 md:px-12 bg-[#111111] border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl font-serif italic text-white mb-6">Ready to Submit?</h2>
          <p className="text-gray-500 mb-10">Create a look that answers the brief. Your next feature starts here.</p>
          {isAuthenticated ? (
            <Link to="/create">
              <Button className="bg-white text-black hover:bg-gray-200 px-8 py-6 text-xs uppercase tracking-[0.2em]">
                Start Your Submission
              </Button>
            </Link>
          ) : (
            <Link to="/auth">
              <Button className="bg-white text-black hover:bg-gray-200 px-8 py-6 text-xs uppercase tracking-[0.2em]">
                Join to Participate
              </Button>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
};

const ChallengesContent = ({ loading, challenges }) => {
  const activeChallenges = challenges.filter(c => c.status === 'active');

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((num) => (
          <div key={`skeleton-${num}`} className="skeleton rounded-sm" style={{ minHeight: '450px' }} />
        ))}
      </div>
    );
  }

  if (activeChallenges.length > 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {activeChallenges.map((challenge) => (
          <ChallengeCard key={challenge.id} challenge={challenge} />
        ))}
      </div>
    );
  }

  return (
    <div className="text-center py-24">
      <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-4" />
      <p className="text-gray-500 text-lg mb-2">No challenges available</p>
      <p className="text-gray-600 text-sm">New briefs are released regularly. Check back soon.</p>
    </div>
  );
};

export default ChallengesPage;
